// Integrations, wearing the ofself shell — the REACT HOST (2026-08-23),
// missions' strangler pattern: React owns the chrome (the landing, the nav
// column, the kit's rail and dock, settings); the legacy engine (public/app.js)
// keeps the router, the api client and the views, painting into the ONE
// leaf React never re-renders (#view-container). Views port out of it one at
// a time; the engine announces state as CustomEvents (ig:auth / ig:identity /
// ig:view / ig:status) and React calls back through its window globals
// (navigateTo, setActiveGroup, signOut, igAccessModel).
import React, { useEffect, useMemo, useState } from 'react';
import {
  Shell,
  ShellProvider,
  SplitView,
  GridNav,
  NavDrawer,
  IdentityWall,
  openShellSettings,
  useWallTab,
} from '@ofself/shell';
import { createPortal } from 'react-dom';
import legacyAppUrl from '../public/app.js?url';
import { loadPermissions, loadActivity, loadLibrary, schemasFrom } from './lib/identityWall.js';
import Automations from './views/Automations.jsx';
import Absorber from './views/Absorber.jsx';
import Plugins from './views/Plugins.jsx';
import Landing from './landing/Landing.jsx';
import { markSoundStore } from './lib/prefs.js';
import SearchSpotlight from './components/SearchSpotlight.jsx';
// THE VIEWS, in the vanilla host's exact order — classic scripts sharing one
// global scope, so ORDER IS BEHAVIOUR (the old index.html's own warning).
import viewTabularUrl from '../public/views/integrations/tabular.js?url';
import viewCronUrl from '../public/views/integrations/cron.js?url';

// tabular.js and cron.js are HELPER LIBRARIES, not views — pure functions the
// React absorber/automations/plugins read off window. Every VIEW is React now;
// app.js remains only as the engine (auth, api client, router, identity).
const LEGACY_SCRIPTS = [legacyAppUrl, viewTabularUrl, viewCronUrl];

// THE PORTED VIEWS — React pages the engine's router yields to (renderView
// clears its own leaf and stands aside; the page portals into the React host
// div beside it).
// 'integrations' aliases 'plugins': the OAuth callback redirects to
// ?view=integrations and old links exist — an alias costs one line.
const REACT_VIEWS = {
  automations: Automations,
  absorber: Absorber,
  plugins: Plugins,
  integrations: Plugins,
};

// ── Nav — the vanilla column's exact glyphs, in GridNav's icon slot ──────────
const NAV = [
  { id: 'absorber', label: 'absorber',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <g opacity="0.45"><path d="M2 2.5L6.1 6.1M14 2.5L9.9 6.1M2 13.5L6.1 9.9M14 13.5L9.9 9.9" /><path d="M8 1v2.5M8 12.5V15M1 8h2.5M12.5 8H15" /><circle cx="8" cy="8" r="2.6" /></g>
        <g fill="var(--shell-ink, #111)" stroke="none"><circle cx="8" cy="8" r="1.1" /></g>
      </svg>
    ) },
  { id: 'plugins', label: 'plugins',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <g opacity="0.45"><circle cx="4.5" cy="4.5" r="2.5" /><circle cx="11.5" cy="4.5" r="2.5" /><circle cx="8" cy="11.5" r="2.5" /><line x1="6.5" y1="5.5" x2="9.5" y2="5.5" /><line x1="5.5" y1="6.5" x2="7" y2="10" /><line x1="10.5" y1="6.5" x2="9" y2="10" /></g>
        <g fill="var(--shell-ink, #111)" stroke="none"><circle cx="4.5" cy="4.5" r="1" /><circle cx="11.5" cy="4.5" r="1" /><circle cx="8" cy="11.5" r="1" /></g>
      </svg>
    ) },
  { id: 'automations', label: 'automations',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M2.5 4h11M2.5 8h7.5M2.5 12h9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ) },
];

// ── The view container — the legacy engine's ONE home (mount once) ───────────
const ViewContainer = React.memo(function ViewContainer() {
  return (
    // no desk (owner call 2026-09-25): the content is its own plain ground
    <div id="main-area" className="block block--fill">
      {/* the legacy engine's canvas — only IT writes here */}
      <div id="view-container" />
      {/* the React pages' host — only REACT writes here */}
      <div id="react-view-host" style={{ height: '100%' }} />
    </div>
  );
});

function ReactViewPortal({ view }) {
  const [host, setHost] = useState(null);
  useEffect(() => { setHost(document.getElementById('react-view-host')); }, []);
  const Page = REACT_VIEWS[view];
  if (!host) return null;
  return createPortal(
    <>
      {Object.entries(REACT_VIEWS).map(([name, Comp]) => (
        <Comp key={name} active={name === view} />
      ))}
    </>,
    host,
  );
}

// ── The access scope — the grant, verb by verb (ported from the vanilla host)
// It lives in the kit's Preferences window now, as this app's own pane: the
// rail's Preferences seat opens it, and the engine's ig:open-access asks for
// it by id. The model is read when the pane renders, so it is never stale.
const ACCESS_PANE = 'ig-access';
function AccessBody({ discoveryDenied }) {
  const { displayName, userId, ep, isActive, expiryLine, resources, tagNodePills } = window.igAccessModel?.() || {};
  return (
    <div className="access-pane">
      <div className="ap-body">
        <div className="ap-user">
          <div className="ap-user-name">{displayName}</div>
          <div className="ap-user-id">{userId || ''}</div>
        </div>
        <div className={`ap-status ${isActive ? 'ap-status-active' : 'ap-status-none'}`}>
          <span className="ap-status-dot" />
          <span>{isActive ? 'access active' : (ep ? 'access inactive' : 'no active grant')}</span>
          <span className="ap-expiry">{expiryLine}</span>
        </div>
        {!ep ? (
          <div className="ap-empty">
            <p>No exposure profile found. This app has not been granted access to your data yet.</p>
            <a href="/auth/connect" className="ap-reconnect-btn">reconnect with paradigm</a>
          </div>
        ) : (
          <>
            {/* THE GRANT CAN BE NARROWER THAN THE ASK, and only this says so.
                discovery:groups is in the DLR, so the app is entitled to
                request it — but an exposure profile minted before it was added
                does not carry it, and /third-party/groups answers 403. That
                403 becomes an empty list with a 200 (routes/groups.js, for the
                good reason that having no groups is normal), so the switcher
                reads "Personal" and nothing anywhere says why. This is the
                only place the difference between "no groups" and "not allowed
                to look" is visible, and it is one re-authorise away. */}
            {discoveryDenied && (
              <div className="ap-resource">
                <div className="ap-resource-hdr">groups</div>
                <div className="ap-vrow ap-denied">
                  <span className="ap-vcheck">✗</span>
                  <span className="ap-vlabel">See which groups you belong to</span>
                </div>
                <div className="ap-scope-sub">
                  Your authorisation predates this request, so the group
                  switcher can only show Personal.{' '}
                  <a href="/auth/connect">Re-authorise to enable it.</a>
                </div>
              </div>
            )}
            {tagNodePills?.length ? (
              <div className="ap-tag-node-scope">
                {tagNodePills.map((p, i) => <span key={i} className="ap-scope-pill">{p}</span>)}
              </div>
            ) : null}
            {(resources || []).map((r) => (
              <div className="ap-resource" key={r.label}>
                <div className="ap-resource-hdr">{r.label}</div>
                {r.rows.map((row, i) => (
                  <React.Fragment key={i}>
                    <div className={`ap-vrow ${row.granted ? 'ap-granted' : 'ap-denied'}`}>
                      <span className="ap-vcheck">{row.granted ? '✓' : '✗'}</span>
                      <span className="ap-vlabel">{row.label}</span>
                    </div>
                    {row.scope && (
                      <div className="ap-scope-sub">
                        <span className="ap-scope-count">{row.scope.count}</span>
                        {row.scope.chips?.length ? (
                          <div className="ap-schema-chips">
                            {row.scope.chips.map((c, j) => <span key={j} className="ap-schema-chip">{c}</span>)}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            ))}
          </>
        )}
        <div className="ap-footer">
          <button className="ap-disconnect-btn" onClick={() => window.signOut?.()}>disconnect</button>
        </div>
      </div>
    </div>
  );
}

const AccessIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="8" cy="8" r="5.5" /><circle cx="8" cy="8" r="1.4" fill="currentColor" stroke="none" />
    <path d="M8 1v2M8 13v2M1 8h2M13 8h2" />
  </svg>
);

// The app's second pane in Preferences: the mark's voice.
function SoundPane() {
  const on = markSoundStore.useStore();
  return (
    <div className="ig-pref">
      <label className="ig-pref-row">
        <input type="checkbox" checked={on} onChange={(e) => markSoundStore.set(e.target.checked)} />
        <span>
          <span className="ig-pref-name">Mark sounds</span>
          <span className="ig-pref-desc">The mark rings like a marimba where your pointer brushes its strings.</span>
        </span>
      </label>
    </div>
  );
}
const SoundIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" aria-hidden="true">
    <path d="M2.5 6v4h2.5l3.5 3V3L5 6z" /><path d="M11 5.5a3.5 3.5 0 0 1 0 5M12.8 3.8a6 6 0 0 1 0 8.4" />
  </svg>
);

function AuthedApp() {
  // ── The engine's announcements ──
  const [identity, setIdentity] = useState({ label: null, displayName: null, groups: [], activeGroupId: null });
  const [view, setView] = useState(null);
  const [status, setStatus] = useState(null); // synthesis status (/process/status)
  // THE WALL — the identity panel, and the one object every tab reads.
  // Fetched ONCE here rather than per tab: four tabs asking the same question
  // four times could show four different answers while a realm was being
  // narrowed underneath them (ink's lesson, same comment).
  const [wallOpen, setWallOpen] = useState(false);
  // null, NOT {}. The kit reads an empty object as a real answer and draws "no
  // authorization"; null means "not known yet" and draws nothing, so the pane
  // does not assert something false for the length of the fetch.
  const [perms, setPerms] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // the wall's reading, shared through its storageKey — search opens a node
  // on the Nucleus ('library') reading
  const [, setWallTab] = useWallTab('ig-identity-wall');

  // Tried at mount AND again when identity settles — not gated behind it. The
  // cookie is usually already valid at mount, so gating would put this behind a
  // second round trip for no gain. Only a truthy answer is written, so an early
  // failure cannot clobber a later good one and the retry costs nothing.
  useEffect(() => {
    let live = true;
    loadPermissions().then((p) => { if (live && p) setPerms(p); });
    return () => { live = false; };
  }, [identity.userId]);

  const wallSchemas = useMemo(() => schemasFrom(perms), [perms]);

  useEffect(() => {
    const onIdent = (e) => setIdentity(e.detail);
    const onView = (e) => setView(e.detail);
    const onStatus = (e) => setStatus(e.detail);
    const onAccess = () => openShellSettings(ACCESS_PANE);
    document.addEventListener('ig:identity', onIdent);
    document.addEventListener('ig:view', onView);
    document.addEventListener('ig:status', onStatus);
    document.addEventListener('ig:open-access', onAccess);
    return () => {
      document.removeEventListener('ig:identity', onIdent);
      document.removeEventListener('ig:view', onView);
      document.removeEventListener('ig:status', onStatus);
      document.removeEventListener('ig:open-access', onAccess);
    };
  }, []);

  // Boot the legacy engine ONCE, after #view-container exists — the scripts
  // are classic and order-dependent, loaded sequentially (async=false).
  useEffect(() => {
    if (window.__igBooted) return;
    window.__igBooted = true;
    // said BEFORE the engine loads, so its router yields from the first route
    window.igReactViews = Object.keys(REACT_VIEWS);
    LEGACY_SCRIPTS.forEach((url, i) => {
      const s = document.createElement('script');
      s.src = url;
      s.async = false;
      // the engine boots only once every view has registered
      if (i === LEGACY_SCRIPTS.length - 1) s.onload = () => window.igInit?.();
      document.body.appendChild(s);
    });
  }, []);

  // THE SYNTHESIS DOT (2026-09-25) rides the Absorber's tile: synthesis runs
  // in the absorber's pipeline (/process), so its state is a fact about that
  // page — told in the tile's sub-line, the kit's slot for a status line. It
  // used to sit on the eco seat, which retired with the old chrome.
  const synth = useMemo(() => {
    if (!status || !Object.keys(status).length) return null;
    const contradictions = (status.contradictions || []).length;
    const patterns = (status.patterns || []).length;
    const running = status.is_running || status.running;
    const cls = running ? 'pulsing' : contradictions > 0 ? 'amber' : 'green';
    const text = running ? 'synthesising'
      : contradictions > 0 ? `${contradictions} contradiction${contradictions !== 1 ? 's' : ''}`
      : patterns > 0 ? `${patterns} pattern${patterns !== 1 ? 's' : ''}`
      : 'up to date';
    return (
      <span className="ig-synth" title={running ? 'synthesis running…' : `identity graph: ${text}`}>
        <span className={`nav-status-dot ${cls}`} aria-hidden="true" />
        <span className="ig-synth__text">{text}</span>
      </span>
    );
  }, [status]);
  const navItems = useMemo(
    () => NAV.map((n) => (n.id === 'absorber' && synth ? { ...n, sub: synth } : n)),
    [synth],
  );

  const accessSettings = useMemo(() => [{
    id: ACCESS_PANE, label: 'Access', icon: <AccessIcon />,
    content: <AccessBody discoveryDenied={identity.discoveryDenied} />,
  }, {
    id: 'ig-sound', label: 'Sound', icon: <SoundIcon />, content: <SoundPane />,
  }], [identity.discoveryDenied]);

  const activeGroup = identity.groups.find((g) => g.id === identity.activeGroupId) || null;
  const groupsAuth = Object.fromEntries(identity.groups.map((g) => [g.id, g.app_authorized !== false]));

  return (
    <ShellProvider
      value={{
        currentUser: { username: identity.displayName || identity.label || 'You' },
        // EVERY group, not only the authorized ones — ink's shape, and for its
        // reason. Filtering here deletes the unauthorized group from the
        // switcher entirely, so the one place that could offer a Connect chip
        // instead shows nothing and the person reads it as "I have no groups".
        // `groupsAuth` below is how the kit tells the two apart; handing it the
        // flag AND hiding the rows it describes was the contradiction.
        groups: identity.groups.map((g) => ({ id: g.id, name: g.name })),
        activeGroup: activeGroup ? { id: activeGroup.id, name: activeGroup.name } : null,
        groupsAuth,
        onSwitchSpace: (space) => window.setActiveGroup?.(space ? space.id : null),
        onSignOut: () => window.signOut?.(),
      }}
    >
      {/* THE FULL RAIL (owner call 2026-09-25, back): the kit's app rail down
          the far left — the mark (home), Search, the Agent, Preferences, Help,
          and the avatar's dock in the last cell. The dock's own row still
          carries Settings and Help too. */}
      <Shell
        currentApp="integrations"
        rail
        dock
        onSearch={() => setSearchOpen((o) => !o)}
        activeSeat={searchOpen ? 'search' : undefined}
        onHome={() => window.navigateTo?.('/absorber')}
        settings={accessSettings}
      >
        {/* THE WORKSPACE — missions' exact anatomy: SplitView leading is the
            page column (.ofself-nav-col, kit-fixed width), trailing is the
            engine's one view container. */}
        <SplitView
          leadingOpen={true}
          // navToken: WITHOUT IT THE COLUMN CANNOT FOLD. SplitView's default
          // uncontrolled mode inlines a width on the pane — clamp(96px, 28vw,
          // 300px) — and NavDrawer's fold works by narrowing
          // --ofself-nav-col-w to the spine. An inline width beats the token, so
          // pressing the grip stamped html.ofself-nav-collapsed, every fold rule
          // fired, and the column went 144px → 96px and stopped: the clamp's own
          // floor. It read as "collapse is broken" when in fact everything
          // worked except the one declaration that decides the width.
          // In navToken mode the drag writes the token instead of an inline
          // width, so the fold, the one-width compound and the hub grids all
          // keep working untouched.
          navToken
          leadingClassName="ig-nav-panel ofself-nav-col"
          leadingProps={{ id: 'sidebar' }}
          leading={
            <>
              <NavDrawer
                items={NAV.map((n) => ({ id: n.id, label: n.label, active: n.id === view }))}
                onSelect={(id) => window.navigateTo?.('/' + id)}
              />
              <GridNav
                orientation="vertical"
                aria-label="Integrations pages"
                items={navItems}
                active={view}
                onSelect={(id) => window.navigateTo?.('/' + id)}
              />
            </>
          }
          trailing={<ViewContainer />}
        />
        {/* THE WALL'S DOOR IS THE RAIL'S AGENT SEAT: `onOpen` registers it.
            Its Personas reading is empty until Integrations has an agent of
            its own to offer; Nucleus, Ledger and Exposure answer.
            strip={false}: no reading strip of its own either.
            agents={[]} IS THE POINT, not an oversight. Omitted, `agents` is
            undefined — "not known yet" — and the kit draws the composer's empty
            state: a + and "Start your first conversation", which here opens
            nothing, because Integrations has no agent surface. [] is the kit's
            way of being told "this app offers no agents" so it says that
            instead. A dead affordance reads as a broken feature; an honest
            absence reads as a boundary.
            initialTab: open on a reading this app actually serves. Personas is
            the kit's default and is the one tab Integrations cannot answer. */}
        <IdentityWall
          docked
          strip={false}
          open={wallOpen}
          onClose={() => setWallOpen(false)}
          onOpen={() => setWallOpen(true)}
          storageKey="ig-identity-wall"
          appName="Integrations"
          identityName={identity.displayName || identity.label || undefined}
          permissions={perms}
          agents={[]}
          initialTab="exposure"
          library={{ load: loadLibrary, schemas: wallSchemas }}
          ledger={{ load: loadActivity }}
        />
        <ReactViewPortal view={view} />
        <SearchSpotlight
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onOpenNode={() => { setWallTab('library'); setWallOpen(true); }}
        />
      </Shell>
    </ShellProvider>
  );
}

export default function App() {
  // THE STAMPED BOOT (the engine's law, kept): the server injects
  // window.__BOOT__ into the signed-in document, so the first frame routes
  // with zero network. The cold path asks /auth/status once.
  const [auth, setAuth] = useState(() => (window.__BOOT__?.userId ? 'authed' : 'checking'));
  const [authMsg, setAuthMsg] = useState('');
  useEffect(() => {
    const onAuth = (e) => {
      if (e.detail?.authed === false) { setAuth('anon'); setAuthMsg(e.detail.message || ''); }
    };
    document.addEventListener('ig:auth', onAuth);
    return () => document.removeEventListener('ig:auth', onAuth);
  }, []);
  useEffect(() => {
    if (auth !== 'checking') return;
    fetch('/auth/status').then((r) => r.json()).then((a) => {
      if (!a.authenticated) { setAuth('anon'); return; }
      if (a.epRevoked) { setAuth('anon'); setAuthMsg('Your access was revoked. Please reconnect.'); return; }
      setAuth('authed');
    }).catch(() => setAuth('anon'));
  }, [auth]);

  if (auth === 'checking') return null;
  if (auth === 'anon') return <Landing message={authMsg} />;
  return <AuthedApp />;
}
